

// src/pages/reports/AssetReportsPage.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchAssetMasterReport,
  fetchAssetAllocationReport,
  fetchIdleAssetsReport,
  fetchAssetLifecycleReport,
  fetchWarrantyExpiryReport,
  exportAssetReport,
  clearError,
  fetchNewAssetLifecycleReport,
} from 'store/slices/reportSlice';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { fetchInventoryCategories, fetchAllInventorySubcategories, fetchEmployeeDropdown, fetchVendorDropdown } from 'store/slices/commonSlice';
import { fetchVendors } from 'store/slices/vendorSlice';

import PageHeader from 'components/common/PageHeader';
import { Card, CardHeader, CardTitle, CardContent } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Input } from 'components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import { Loader2, Package, User, Calendar, AlertCircle, DollarSign, Clock, Users, Box, Download, Filter, X, ChevronDown, ChevronUp } from 'lucide-react';
import { format, parseISO, isValid } from 'date-fns';
import { toast } from 'sonner';
import LogDateFilter from 'components/common/LogDateFilter';
import DataTable from 'components/common/DataTable';
import FormField from 'components/common/FormField';

const AssetReportsPage = () => {
  const dispatch = useDispatch();
  const {
    assetMaster,
    assetAllocation,
    idleAssets,
    assetLifecycle,
    warrantyExpiry,
    newAssetLifecycle,
    isLoading,
    error,
  } = useSelector((state) => state.report);

  const [activeTab, setActiveTab] = useState('master');
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedAssetHistory, setSelectedAssetHistory] = useState(null);

  // Advanced filter states for each tab
  const [masterFilters, setMasterFilters] = useState({
    category: 'all',
    sub_category: 'all',
    vendor: 'all',
    is_assigned: 'all',
    min_quantity: '',
    max_quantity: '',
    min_value: '',
    max_value: '',
  });

  const [allocationFilters, setAllocationFilters] = useState({
    employee: 'all',
    category: 'all',
    sub_category: 'all',
  });

  const [idleFilters, setIdleFilters] = useState({
    category: 'all',
    sub_category: 'all',
    min_quantity: '',
    max_quantity: '',
  });

  const [lifecycleFilters, setLifecycleFilters] = useState({
    category: 'all',
    sub_category: 'all',
  });

  const [warrantyFilters, setWarrantyFilters] = useState({
    category: 'all',
    sub_category: 'all',
    vendor: 'all',
    is_assigned: 'all',
  });


  const [assetLifecycleFilters, setAssetLifecycleFilters] = useState({
    category: 'all',
    sub_category: 'all',
  });

  // Get current filters based on active tab
  const advancedFilters = {
    master: masterFilters,
    allocation: allocationFilters,
    idle: idleFilters,
    lifecycle: lifecycleFilters,
    warranty: warrantyFilters,
    assetLifecycle: lifecycleFilters,
  }[activeTab];

  // Get data from Redux state
  const inventoryCategories = useSelector((state) => state.common?.inventoryCategories?.items || []);

  const subcategories = useSelector((state) => state.common?.allInventorySubcategories.items || []);

  const employees = useSelector((state) => state.common?.employees?.items || []);
  const employeesLoading = useSelector((state) => state.common?.employees?.loading || false);
  const vendors = useSelector((state) => state.common?.vendors?.items || []);


  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilters, setDateFilters] = useState({});
  const [warrantyDays, setWarrantyDays] = useState(30);
  const [isAdvancedFilterExpanded, setIsAdvancedFilterExpanded] = useState(false);




  // Check if any advanced filters are active (excluding 'all' values)
  const hasActiveAdvancedFilters = Object.values(advancedFilters).some(value =>
    value !== '' && value !== 'all'
  );

  // Reset page when filters or tab change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, dateFilters, activeTab, warrantyDays, advancedFilters]);

  // Initial parallel load of all reports
  useEffect(() => {
    const commonParams = { page: 1, page_size: 10 };
    dispatch(fetchAssetMasterReport(commonParams));
    dispatch(fetchAssetAllocationReport(commonParams));
    dispatch(fetchIdleAssetsReport(commonParams));
    dispatch(fetchAssetLifecycleReport(commonParams));
    dispatch(fetchWarrantyExpiryReport({ ...commonParams, days: 30 }));

    // Fetch dropdown data
    dispatch(fetchInventoryCategories());
    dispatch(fetchAllInventorySubcategories());
    dispatch(fetchEmployeeDropdown({}));
    dispatch(fetchVendorDropdown());
  }, [dispatch]);


  // Refetch current tab data
  const refetch = useCallback(() => {
    const commonParams = {
      page,
      page_size: pageSize,
      search: searchTerm.trim() || undefined,
      ...dateFilters,
    };

    // Add tab-specific filters
    const tabSpecificParams = {
      ...(activeTab === 'master' && {
        category: advancedFilters.category !== 'all' ? advancedFilters.category : undefined,
        sub_category: advancedFilters.sub_category !== 'all' ? advancedFilters.sub_category : undefined,
        vendor: advancedFilters.vendor !== 'all' ? advancedFilters.vendor : undefined,
        is_assigned: advancedFilters.is_assigned !== 'all' ? advancedFilters.is_assigned : undefined,
        min_quantity: advancedFilters.min_quantity || undefined,
        max_quantity: advancedFilters.max_quantity || undefined,
        min_value: advancedFilters.min_value || undefined,
        max_value: advancedFilters.max_value || undefined,
      }),
      ...(activeTab === 'allocation' && {
        employee: advancedFilters.employee !== 'all' ? advancedFilters.employee : undefined,
        category: advancedFilters.category !== 'all' ? advancedFilters.category : undefined,
        sub_category: advancedFilters.sub_category !== 'all' ? advancedFilters.sub_category : undefined,
      }),
      ...(activeTab === 'idle' && {
        category: advancedFilters.category !== 'all' ? advancedFilters.category : undefined,
        sub_category: advancedFilters.sub_category !== 'all' ? advancedFilters.sub_category : undefined,
        min_quantity: advancedFilters.min_quantity || undefined,
        max_quantity: advancedFilters.max_quantity || undefined,
      }),
      ...((activeTab === 'lifecycle' || activeTab === 'assetLifecycle') && {
        category: advancedFilters.category !== 'all' ? advancedFilters.category : undefined,
        sub_category: advancedFilters.sub_category !== 'all' ? advancedFilters.sub_category : undefined,
      }),
      ...(activeTab === 'warranty' && {
        category: advancedFilters.category !== 'all' ? advancedFilters.category : undefined,
        sub_category: advancedFilters.sub_category !== 'all' ? advancedFilters.sub_category : undefined,
        vendor: advancedFilters.vendor !== 'all' ? advancedFilters.vendor : undefined,
        is_assigned: advancedFilters.is_assigned !== 'all' ? advancedFilters.is_assigned : undefined,
      }),
    };

    switch (activeTab) {
      case 'master':
        dispatch(fetchAssetMasterReport({ ...commonParams, ...tabSpecificParams }));
        break;
      case 'allocation':
        dispatch(fetchAssetAllocationReport({ ...commonParams, ...tabSpecificParams }));
        break;
      case 'idle':
        dispatch(fetchIdleAssetsReport({ ...commonParams, ...tabSpecificParams }));
        break;
      case 'lifecycle':
        dispatch(fetchAssetLifecycleReport({ ...commonParams, ...tabSpecificParams }));
        break;
      case 'assetLifecycle':  // ← NEW TAB
        dispatch(fetchNewAssetLifecycleReport({ ...commonParams, ...tabSpecificParams }));
        break;
      case 'warranty':
        dispatch(fetchWarrantyExpiryReport({ ...commonParams, ...tabSpecificParams, days: warrantyDays }));
        break;
      default:
        break;
    }
  }, [dispatch, activeTab, page, pageSize, searchTerm, dateFilters, warrantyDays, advancedFilters]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Current report data
  const currentReport = {
    master: assetMaster,
    allocation: assetAllocation,
    idle: idleAssets,
    lifecycle: assetLifecycle,
    warranty: warrantyExpiry,
    assetLifecycle: newAssetLifecycle,
  }[activeTab] || { summary: {}, data: [], pagination: { total_pages: 1, count: 0 } };

  const dataList = currentReport.data || [];
  const summary = currentReport.summary || {};
  const pagination = currentReport.pagination || { total_pages: 1, count: 0 };

  // Helper function to safely format dates
  const formatDateSafely = (dateString) => {
    if (!dateString || dateString === '-') return '-';

    try {
      // Try to parse as ISO date first
      const date = parseISO(dateString);
      if (isValid(date)) {
        return format(date, 'dd MMM yyyy');
      }

      // If ISO parsing fails, try parsing as YYYY-MM-DD
      const parts = dateString.split('-');
      if (parts.length === 3) {
        const parsedDate = new Date(parts[0], parts[1] - 1, parts[2]);
        if (isValid(parsedDate)) {
          return format(parsedDate, 'dd MMM yyyy');
        }
      }

      // If all parsing fails, return the original string
      return dateString || '-';
    } catch (error) {
      console.error('Date formatting error:', error, 'for date:', dateString);
      return dateString || '-';
    }
  };

  // --- Event Handlers ---
  const handleFilterChange = (key, value) => {
    const filterSetters = {
      master: setMasterFilters,
      allocation: setAllocationFilters,
      idle: setIdleFilters,
      lifecycle: setLifecycleFilters,
      assetLifecycle: setLifecycleFilters,
      warranty: setWarrantyFilters,
    };

    filterSetters[activeTab](prev => ({
      ...prev,
      [key]: value,
      // Reset subcategory when category changes
      ...(key === 'category' && { sub_category: 'all' })
    }));
  };

  const resetFilters = () => {
    const filterResetters = {
      master: () => setMasterFilters({
        category: 'all',
        sub_category: 'all',
        vendor: 'all',
        is_assigned: 'all',
        min_quantity: '',
        max_quantity: '',
        min_value: '',
        max_value: '',
      }),
      allocation: () => setAllocationFilters({
        employee: 'all',
        category: 'all',
        sub_category: 'all',
      }),
      idle: () => setIdleFilters({
        category: 'all',
        sub_category: 'all',
        min_quantity: '',
        max_quantity: '',
      }),
      lifecycle: () => setLifecycleFilters({
        category: 'all',
        sub_category: 'all',
      }),
      warranty: () => setWarrantyFilters({
        category: 'all',
        sub_category: 'all',
        vendor: 'all',
        is_assigned: 'all',
      }),
      assetLifecycle: () => setLifecycleFilters({  // ← ADD THIS LINE
        category: 'all',
        sub_category: 'all',
      }),
    };

    filterResetters[activeTab]();
  };

  // ==================== TABLE COLUMNS ====================

  const masterColumns = [
    // { accessorKey: 'category__name', header: 'Category' },
    { accessorKey: 'sub_category__name', header: 'Sub Category' },
    { accessorKey: 'vendor__name', header: 'Vendor', cell: ({ row }) => row.original.vendor__name || '-' },
    { accessorKey: 'serial_no', header: 'Serial No.', cell: ({ row }) => row.original.serial_no || '-' },
    { accessorKey: 'brand_name', header: 'Brand', cell: ({ row }) => row.original.brand_name || '-' },
    { accessorKey: 'model_no', header: 'Model', cell: ({ row }) => row.original.model_no || '-' },
    { accessorKey: 'invoice_no', header: 'Invoice No.', cell: ({ row }) => row.original.invoice_no || '-' },
    { accessorKey: 'quantity', header: 'Quantity' },
    { accessorKey: 'purchased_on', header: 'Purchased On', cell: ({ row }) => formatDateSafely(row.original.purchased_on) },
    {
      accessorKey: 'warranty_expired_on',
      header: 'Warranty Expiry',
      cell: ({ row }) => row.original.warranty_expired_on ? formatDateSafely(row.original.warranty_expired_on) : '-',
    },
    {
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={row.original.is_assigned ? 'success' : 'secondary'}>
          {row.original.is_assigned ? 'Assigned' : 'Available'}
        </Badge>
      ),
    },
    {
      accessorKey: 'assigned_to',
      header: 'Assigned To',
      cell: ({ row }) => row.original.assigned_to || 'Unassigned',
    },
    {
      accessorKey: 'created_at',
      header: 'Added On',
      cell: ({ row }) => formatDateSafely(row.original.created_at),
    },
  ];

  const allocationColumns = [
    {
      header: 'Employee',
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.employee_name || 'Unknown'}</div>
          <div className="text-xs text-muted-foreground">{row.original.asset_assign__employee__code || '-'}</div>
        </div>
      ),
    },
    // { accessorKey: 'inventory__category__name', header: 'Category' },
    { accessorKey: 'inventory__sub_category__name', header: 'Asset Type' },
    { accessorKey: 'inventory__serial_no', header: 'Serial No.', cell: ({ row }) => row.original.inventory__serial_no || '-' },
    { accessorKey: 'inventory__brand_name', header: 'Brand', cell: ({ row }) => row.original.inventory__brand_name || '-' },
    { accessorKey: 'inventory__model_no', header: 'Model', cell: ({ row }) => row.original.inventory__model_no || '-' },
    { accessorKey: 'inventory__quantity', header: 'Quantity' },
    {
      accessorKey: 'assigned_on',
      header: 'Assigned On',
      cell: ({ row }) => formatDateSafely(row.original.assigned_on),
    },
  ];

  const idleColumns = [
    { accessorKey: 'sub_category__name', header: 'Asset Type' },
    { accessorKey: 'serial_no', header: 'Serial No.', cell: ({ row }) => row.original.serial_no || 'N/A' },
    {
      header: 'Available Qty',
      cell: ({ row }) => (
        <Badge variant="outline" className="font-bold">
          {row.original.quantity || 0}
        </Badge>
      ),
    },
    {
      accessorKey: 'created_at',
      header: 'Last Updated',
      cell: ({ row }) => formatDateSafely(row.original.created_at),
    },
  ];

  const lifecycleColumns = [
    { accessorKey: 'sub_category__name', header: 'Asset Type' },
    { header: 'Total Assets', cell: ({ row }) => <span className="font-bold">{row.original.asset_count || 0}</span> },
    {
      header: 'Total Cost',
      cell: ({ row }) => (
        <span className="font-bold text-green-600">
          ₹{Number(row.original.total_cost || 0).toLocaleString('en-IN')}
        </span>
      ),
    },
    {
      header: 'Avg Cost',
      cell: ({ row }) => (
        <span className="text-muted-foreground">
          ₹{Number(row.original.avg_cost || 0).toLocaleString('en-IN')}
        </span>
      ),
    },
  ];

  const assetLifecycleColumns = [
    {
      header: 'Asset',
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.sub_category}</div>
          <div className="text-xs text-muted-foreground">
            Serial: {row.original.serial_no || 'N/A'}
          </div>
        </div>
      ),
    },
    { accessorKey: 'brand_name', header: 'Brand' },
    { accessorKey: 'model_no', header: 'Model' },
    {
      accessorKey: 'purchase_date',
      header: 'Purchased On',
      cell: ({ row }) => formatDateSafely(row.original.purchase_date),
    },
    {
      accessorKey: 'warranty_expiry',
      header: 'Warranty Expiry',
      cell: ({ row }) => {
        const expiry = row.original.warranty_expiry;
        const isExpired = row.original.is_expired;
        return (
          <div className="flex items-center gap-2">
            {expiry ? formatDateSafely(expiry) : '-'}
            {isExpired && <Badge variant="destructive">Expired</Badge>}
          </div>
        );
      },
    },
    {
      header: 'Current Status',
      cell: ({ row }) => (
        <Badge variant={row.original.current_status === 'Assigned' ? 'success' : 'secondary'}>
          {row.original.current_status}
        </Badge>
      ),
    },
    {
      header: 'Assignments',
      cell: ({ row }) => (
        <Badge variant="outline">{row.original.assignment_count || 0}</Badge>
      ),
    },
    {
      header: 'Actions',
      cell: ({ row }) => (
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setSelectedAssetHistory(row.original);
            setHistoryModalOpen(true);
          }}
          className="h-8 px-3 text-xs"
        >
          <Calendar className="h-3.5 w-3.5 mr-1" />
          View History
        </Button>
      ),
    },
  ];

  const warrantyColumns = [
    { accessorKey: 'sub_category__name', header: 'Asset Type' },
    { accessorKey: 'serial_no', header: 'Serial No.', cell: ({ row }) => row.original.serial_no || 'N/A' },
    { accessorKey: 'brand_name', header: 'Brand', cell: ({ row }) => row.original.brand_name || '-' },
    { accessorKey: 'model_no', header: 'Model', cell: ({ row }) => row.original.model_no || '-' },
    {
      header: 'Warranty Expiry',
      cell: ({ row }) => {
        if (!row.original.warranty_expired_on) return <span className="text-muted-foreground">No Warranty</span>;
        const expiryDate = parseISO(row.original.warranty_expired_on);
        const daysLeft = Math.ceil((expiryDate - new Date()) / (1000 * 60 * 60 * 24));
        return (
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            <span>{formatDateSafely(row.original.warranty_expired_on)}</span>
            {daysLeft <= 7 && <Badge variant="destructive">Expiring Soon</Badge>}
            {daysLeft < 0 && <Badge variant="destructive">Expired</Badge>}
          </div>
        );
      },
    },
    {
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={row.original.is_assigned ? 'success' : 'secondary'}>
          {row.original.is_assigned ? 'Assigned' : 'Available'}
        </Badge>
      ),
    },
  ];

  // ==================== DYNAMIC SUMMARY CARDS ====================

  const renderSummaryCards = () => {
    switch (activeTab) {
      case 'master':
        return (
          <>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2"><Package className="h-4 w-4" />Total Assets</CardTitle>
              </CardHeader>
              <CardContent><div className="text-2xl font-bold">{summary.total_assets || 0}</div></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2"><User className="h-4 w-4 text-blue-600" />Assigned</CardTitle>
              </CardHeader>
              <CardContent><div className="text-2xl font-bold text-blue-600">{summary.assigned || 0}</div></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2"><Box className="h-4 w-4 text-orange-600" />Available</CardTitle>
              </CardHeader>
              <CardContent><div className="text-2xl font-bold text-orange-600">{summary.unassigned || 0}</div></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2"><DollarSign className="h-4 w-4 text-green-600" />Total Value</CardTitle>
              </CardHeader>
              <CardContent><div className="text-2xl font-bold text-green-600">₹{(summary.total_value || 0).toLocaleString('en-IN')}</div></CardContent>
            </Card>
          </>
        );

      case 'allocation':
        return (
          <>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-2"><Users className="h-4 w-4" />Total Allocations</CardTitle></CardHeader>
              <CardContent><div className="text-2xl font-bold">{summary.total_allocations || 0}</div></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-2"><User className="h-4 w-4 text-blue-600" />Unique Employees</CardTitle></CardHeader>
              <CardContent><div className="text-2xl font-bold text-blue-600">{summary.unique_employees || 0}</div></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-2"><Package className="h-4 w-4 text-purple-600" />Total Assets Allocated</CardTitle></CardHeader>
              <CardContent><div className="text-2xl font-bold text-purple-600">{summary.total_assets_allocated || 0}</div></CardContent>
            </Card>
          </>
        );

      case 'idle':
        return (
          <>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-2"><Clock className="h-4 w-4 text-orange-600" />Total Idle Items</CardTitle></CardHeader>
              <CardContent><div className="text-2xl font-bold text-orange-600">{summary.total_idle_items || 0}</div></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-2"><Box className="h-4 w-4 text-purple-600" />Total Idle Quantity</CardTitle></CardHeader>
              <CardContent><div className="text-2xl font-bold text-purple-600">{summary.total_idle_quantity || 0}</div></CardContent>
            </Card>
          </>
        );

      case 'lifecycle':
        return (
          <>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-2"><Package className="h-4 w-4" />Overall Asset Count</CardTitle></CardHeader>
              <CardContent><div className="text-2xl font-bold">{summary.overall_asset_count || 0}</div></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-2"><DollarSign className="h-4 w-4 text-green-600" />Overall Total Cost</CardTitle></CardHeader>
              <CardContent><div className="text-2xl font-bold text-green-600">₹{(summary.overall_total_cost || 0).toLocaleString('en-IN')}</div></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-2"><DollarSign className="h-4 w-4 text-muted-foreground" />Average Cost</CardTitle></CardHeader>
              <CardContent><div className="text-2xl font-bold text-muted-foreground">₹{(summary.overall_average_cost || 0).toLocaleString('en-IN')}</div></CardContent>
            </Card>
          </>
        );

      case 'assetLifecycle':
        return (
          <>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Package className="h-4 w-4" /> Total IT Items
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{summary["Total IT Items"] || 0}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <User className="h-4 w-4 text-blue-600" /> Currently Assigned
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">{summary["Currently Assigned"] || 0}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Box className="h-4 w-4 text-orange-600" /> Returned Items
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600">{summary["Returned Items"] || 0}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-destructive" /> Expired Warranties
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-destructive">{summary["Expired Warranties"] || 0}</div>
              </CardContent>
            </Card>
          </>
        );

      case 'warranty':
        return (
          <>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-2"><AlertCircle className="h-4 w-4 text-destructive" />Expiring Soon (Total)</CardTitle></CardHeader>
              <CardContent><div className="text-2xl font-bold text-destructive">{summary.total_expiring_soon || 0}</div></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-2"><Calendar className="h-4 w-4 text-orange-600" />Expiring in 7 Days</CardTitle></CardHeader>
              <CardContent><div className="text-2xl font-bold text-orange-600">{summary.expiring_in_7_days || 0}</div></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-2"><User className="h-4 w-4 text-blue-600" />Assigned</CardTitle></CardHeader>
              <CardContent><div className="text-2xl font-bold text-blue-600">{summary.assigned_count || 0}</div></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-2"><Box className="h-4 w-4" />Unassigned</CardTitle></CardHeader>
              <CardContent><div className="text-2xl font-bold">{summary.unassigned_count || 0}</div></CardContent>
            </Card>
          </>
        );

      default:
        return null;
    }
  };

  // ==================== EXPORT HANDLER ====================

  const handleExport = async () => {
    toast.loading('Preparing CSV export...');

    const params = {
      search: searchTerm.trim() || undefined,
      ...dateFilters,
      ...(activeTab === 'warranty' ? { days: warrantyDays } : {}),
    };

    // Add tab-specific filters to export params
    if (activeTab === 'master') {
      if (advancedFilters.category !== 'all') params.category = advancedFilters.category;
      if (advancedFilters.sub_category !== 'all') params.sub_category = advancedFilters.sub_category;
      if (advancedFilters.vendor !== 'all') params.vendor = advancedFilters.vendor;
      if (advancedFilters.is_assigned !== 'all') params.is_assigned = advancedFilters.is_assigned;
      if (advancedFilters.min_quantity) params.min_quantity = advancedFilters.min_quantity;
      if (advancedFilters.max_quantity) params.max_quantity = advancedFilters.max_quantity;
      if (advancedFilters.min_value) params.min_value = advancedFilters.min_value;
      if (advancedFilters.max_value) params.max_value = advancedFilters.max_value;
    } else if (activeTab === 'allocation') {
      if (advancedFilters.employee !== 'all') params.employee = advancedFilters.employee;
      if (advancedFilters.category !== 'all') params.category = advancedFilters.category;
      if (advancedFilters.sub_category !== 'all') params.sub_category = advancedFilters.sub_category;
    } else if (activeTab === 'idle') {
      if (advancedFilters.category !== 'all') params.category = advancedFilters.category;
      if (advancedFilters.sub_category !== 'all') params.sub_category = advancedFilters.sub_category;
      if (advancedFilters.min_quantity) params.min_quantity = advancedFilters.min_quantity;
      if (advancedFilters.max_quantity) params.max_quantity = advancedFilters.max_quantity;
    } else if (activeTab === 'lifecycle' || activeTab === 'assetLifecycle') {
      if (advancedFilters.category !== 'all') params.category = advancedFilters.category;
      if (advancedFilters.sub_category !== 'all') params.sub_category = advancedFilters.sub_category;
    } else if (activeTab === 'warranty') {
      if (advancedFilters.category !== 'all') params.category = advancedFilters.category;
      if (advancedFilters.sub_category !== 'all') params.sub_category = advancedFilters.sub_category;
      if (advancedFilters.vendor !== 'all') params.vendor = advancedFilters.vendor;
      if (advancedFilters.is_assigned !== 'all') params.is_assigned = advancedFilters.is_assigned;
    }

    const typeMap = {
      master: 'asset_master',
      allocation: 'allocation',
      idle: 'idle',
      lifecycle: 'lifecycle',
      assetLifecycle: 'it_assignment_inventory_life_cycle',
      warranty: 'warranty',
    };

    const result = await dispatch(exportAssetReport({ type: typeMap[activeTab], params }));

    if (exportAssetReport.fulfilled.match(result)) {
      const { blob } = result.payload;
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      const tabName = activeTab.charAt(0).toUpperCase() + activeTab.slice(1);
      link.setAttribute('download', `${tabName}_Report_${format(new Date(), 'yyyy-MM-dd')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.dismiss();
      toast.success('CSV exported successfully!');
    } else {
      toast.dismiss();
      toast.error('Export failed. Please try again.');
    }
  };

  // ==================== RENDER ====================

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Asset Reports Dashboard"
        description="Comprehensive insights into asset inventory, allocation, utilization, costs, and warranty status"
      />

      <Card>
        <CardContent className="pt-6">
          <LogDateFilter onFilter={(f) => setDateFilters(f)} />
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
        {renderSummaryCards()}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-1 p-1 bg-muted rounded-lg">
          <TabsTrigger value="master" className="data-[state=active]:bg-background data-[state=active]:shadow-sm">
            <Package className="h-4 w-4 mr-2" />Master
          </TabsTrigger>
          <TabsTrigger value="allocation" className="data-[state=active]:bg-background data-[state=active]:shadow-sm">
            <User className="h-4 w-4 mr-2" />Allocation
          </TabsTrigger>
          <TabsTrigger value="idle" className="data-[state=active]:bg-background data-[state=active]:shadow-sm">
            <Clock className="h-4 w-4 mr-2" />Idle
          </TabsTrigger>
          <TabsTrigger value="lifecycle" className="data-[state=active]:bg-background data-[state=active]:shadow-sm">
            <DollarSign className="h-4 w-4 mr-2" />Purchase Lifecycle
          </TabsTrigger>
          <TabsTrigger value="assetLifecycle" className="data-[state=active]:bg-background data-[state=active]:shadow-sm">
            <DollarSign className="h-4 w-4 mr-2" />Asset Inventory Lifecycle
          </TabsTrigger>
          <TabsTrigger value="warranty" className="data-[state=active]:bg-background data-[state=active]:shadow-sm">
            <AlertCircle className="h-4 w-4 mr-2" />Warranty
          </TabsTrigger>
        </TabsList>

        {/* Advanced Filters Card */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Filter className="h-5 w-5" />
                Advanced Filters
                {hasActiveAdvancedFilters && (
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                    Active
                  </span>
                )}
              </CardTitle>
              <div className="flex gap-2">
                {hasActiveAdvancedFilters && (
                  <Button variant="outline" size="sm" onClick={resetFilters}>
                    <X className="h-4 w-4 mr-1" />
                    Reset
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsAdvancedFilterExpanded(!isAdvancedFilterExpanded)}
                >
                  {isAdvancedFilterExpanded ? (
                    <>
                      Hide <ChevronUp className="h-4 w-4 ml-1" />
                    </>
                  ) : (
                    <>
                      Show <ChevronDown className="h-4 w-4 ml-1" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardHeader>
          {isAdvancedFilterExpanded && (
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Common filters for all tabs */}
                {/* <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Select
                    value={advancedFilters.category}
                    onValueChange={(value) => handleFilterChange('category', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {Array.isArray(inventoryCategories) && inventoryCategories.map((category) => (
                        <SelectItem key={category.id} value={category.id}>
                          {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sub_category">Sub Category</Label>
                  <Select
                    value={advancedFilters.sub_category}
                    onValueChange={(value) => handleFilterChange('sub_category', value)}

                  >
                    <SelectTrigger>
                      <SelectValue placeholder=

                        "Select subcategory"
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Subcategories</SelectItem>
                      {Array.isArray(subcategories) && subcategories.map((subcategory) => (
                        <SelectItem key={subcategory.id} value={subcategory.id}>
                          {subcategory.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div> */}
                {/* <FormField
  label="Category"
  name="category"
  type="select"
  value={advancedFilters.category}
  onChange={(e) => handleFilterChange('category', e.target.value)}
  options={[
    { value: 'all', label: 'All Categories' },
    ...(Array.isArray(inventoryCategories)
      ? inventoryCategories.map((category) => ({
          value: String(category.id),
          label: category.name,
        }))
      : []),
  ]}
/> */}

                <FormField
                  label="Sub Category"
                  name="sub_category"
                  type="select"
                  value={advancedFilters.sub_category}
                  onChange={(e) => handleFilterChange('sub_category', e.target.value)}
                  options={[
                    { value: 'all', label: 'All Subcategories' },
                    ...(Array.isArray(subcategories)
                      ? subcategories.map((subcategory) => ({
                        value: String(subcategory.id),
                        label: subcategory.name,
                      }))
                      : []),
                  ]}
                />


                {/* Tab-specific filters */}
                {/* {activeTab === 'master' && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="vendor">Vendor</Label>
                      <Select
                        value={advancedFilters.vendor}
                        onValueChange={(value) => handleFilterChange('vendor', value)}
                        
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select vendor" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Vendors</SelectItem>
                          {Array.isArray(vendors) && vendors.map((vendor) => (
                            <SelectItem key={vendor.id} value={vendor.id}>
                              {vendor.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="is_assigned">Assigned Status</Label>
                      <Select
                        value={advancedFilters.is_assigned}
                        onValueChange={(value) => handleFilterChange('is_assigned', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          <SelectItem value="assigned">Assigned</SelectItem>
                          <SelectItem value="unassigned">Unassigned</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Quantity Range</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Input
                            type="number"
                            placeholder="Min"
                            value={advancedFilters.min_quantity}
                            onChange={(e) => handleFilterChange('min_quantity', e.target.value)}
                          />
                        </div>
                        <div>
                          <Input
                            type="number"
                            placeholder="Max"
                            value={advancedFilters.max_quantity}
                            onChange={(e) => handleFilterChange('max_quantity', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Value Range (₹)</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Input
                            type="number"
                            placeholder="Min"
                            value={advancedFilters.min_value}
                            onChange={(e) => handleFilterChange('min_value', e.target.value)}
                          />
                        </div>
                        <div>
                          <Input
                            type="number"
                            placeholder="Max"
                            value={advancedFilters.max_value}
                            onChange={(e) => handleFilterChange('max_value', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {activeTab === 'allocation' && (
                  <div className="space-y-2">
                    <Label htmlFor="employee">Employee</Label>
                    <Select
                      value={advancedFilters.employee}
                      onValueChange={(value) => handleFilterChange('employee', value)}
                      disabled={employeesLoading}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={employeesLoading ? "Loading..." : "Select employee"} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Employees</SelectItem>
                        {Array.isArray(employees) && employees.map((employee) => (
                          <SelectItem key={employee.id} value={employee.id}>
                            {employee.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )} */}

                {activeTab === 'master' && (
                  <>
                    <FormField
                      label="Vendor"
                      name="vendor"
                      type="select"
                      value={advancedFilters.vendor}
                      onChange={(e) => handleFilterChange('vendor', e.target.value)}
                      options={[
                        { value: 'all', label: 'All Vendors' },
                        ...(Array.isArray(vendors)
                          ? vendors.map((vendor) => ({
                            value: String(vendor.id),
                            label: vendor.name,
                          }))
                          : []),
                      ]}
                    />
                  </>
                )}

                {activeTab === 'allocation' && (
                  <FormField
                    label="Employee"
                    name="employee"
                    type="select"
                    value={advancedFilters.employee}
                    onChange={(e) => handleFilterChange('employee', e.target.value)}
                    disabled={employeesLoading}
                    options={[
                      { value: 'all', label: 'All Employees' },
                      ...(Array.isArray(employees)
                        ? employees.map((employee) => ({
                          value: String(employee.id),
                          label: employee.name,
                        }))
                        : []),
                    ]}
                  />
                )}

                {(activeTab === 'idle') && (
                  <div className="space-y-2">
                    <Label>Quantity Range</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Input
                          type="number"
                          placeholder="Min"
                          value={advancedFilters.min_quantity}
                          onChange={(e) => handleFilterChange('min_quantity', e.target.value)}
                        />
                      </div>
                      <div>
                        <Input
                          type="number"
                          placeholder="Max"
                          value={advancedFilters.max_quantity}
                          onChange={(e) => handleFilterChange('max_quantity', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'warranty' && (
                  <>
                    {/* <div className="space-y-2">
                      <Label htmlFor="vendor">Vendor</Label>
                      <Select
                        value={advancedFilters.vendor}
                        onValueChange={(value) => handleFilterChange('vendor', value)}
                      
                      >
                        <SelectTrigger>
                          <SelectValue placeholder= "Select vendor" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Vendors</SelectItem>
                          {Array.isArray(vendors) && vendors.map((vendor) => (
                            <SelectItem key={vendor.id} value={vendor.id}>
                              {vendor.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div> */}
                    <FormField
                      label="Vendor"
                      name="vendor"
                      type="select"
                      value={advancedFilters.vendor}
                      onChange={(e) =>
                        handleFilterChange('vendor', e.target.value)
                      }
                      options={[
                        { value: 'all', label: 'All Vendors' },
                        ...(Array.isArray(vendors)
                          ? vendors.map((vendor) => ({
                            value: String(vendor.id),
                            label: vendor.name,
                          }))
                          : []),
                      ]}
                    />


                    <div className="space-y-2">
                      <Label htmlFor="is_assigned">Assigned Status</Label>
                      <Select
                        value={advancedFilters.is_assigned}
                        onValueChange={(value) => handleFilterChange('is_assigned', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          <SelectItem value="true">Assigned</SelectItem>
                          <SelectItem value="false">Unassigned</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          )}
        </Card>

        {/* Warranty filter */}
        {/* {activeTab === 'warranty' && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Warranty Expiry Window</CardTitle>
                <div className="flex gap-2 flex-wrap">
                  {[7, 15, 30, 60, 90].map((days) => (
                    <Badge
                      key={days}
                      variant={warrantyDays === days ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => setWarrantyDays(days)}
                    >
                      Next {days} Days
                    </Badge>
                  ))}
                </div>
              </div>
            </CardHeader>
          </Card>
        )} */}

        <TabsContent value={activeTab} className="space-y-6">
          <DataTable
            data={dataList}
            columns={
              activeTab === 'master' ? masterColumns :
                activeTab === 'allocation' ? allocationColumns :
                  activeTab === 'idle' ? idleColumns :
                    activeTab === 'lifecycle' ? lifecycleColumns :
                      activeTab === 'assetLifecycle' ? assetLifecycleColumns :
                        warrantyColumns
            }
            totalCount={pagination.count}
            searchPlaceholder="Search in this report..."
            onSearch={setSearchTerm}
            showSearch={true}
            pageIndex={page - 1}
            pageSize={pageSize}
            pageCount={pagination.total_pages || 1}
            onPageChange={(p) => setPage(p + 1)}
            onPageSizeChange={setPageSize}
            isLoading={isLoading}
            showExport={true}
            onExport={handleExport}
            showImport={false}
          />
        </TabsContent>
      </Tabs>

      {/* History Modal */}
      <Dialog open={historyModalOpen} onOpenChange={setHistoryModalOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Asset Lifecycle History
            </DialogTitle>
            <DialogDescription>
              {selectedAssetHistory && (
                <div className="mt-2 space-y-1">
                  <div className="font-medium">{selectedAssetHistory.sub_category}</div>
                  <div className="text-sm text-muted-foreground">
                    Serial: {selectedAssetHistory.serial_no || 'N/A'} •
                    Brand: {selectedAssetHistory.brand_name} •
                    Model: {selectedAssetHistory.model_no}
                  </div>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="mt-6 space-y-4">
            {selectedAssetHistory?.history?.length > 0 ? (
              selectedAssetHistory.history.map((event, i) => (
                <div key={i} className="flex gap-4 border-l-4 border-primary/50 pl-4 py-3 bg-muted/30 rounded-r-lg">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      {event.event === 'Purchased' && <DollarSign className="h-5 w-5 text-green-600" />}
                      {event.event === 'Assigned' && <User className="h-5 w-5 text-blue-600" />}
                      {event.event === 'Create' && <Package className="h-5 w-5 text-purple-600" />}
                      {event.event === 'Update' && <Package className="h-5 w-5 text-orange-600" />}
                      {(event.event === 'Returned' || event.event.includes('returned')) && <Box className="h-5 w-5 text-orange-600" />}
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-foreground">{event.event}</div>
                    <div className="text-sm text-muted-foreground">
                      {formatDateSafely(event.timestamp)}
                    </div>
                    {event.details && (
                      <div className="mt-2 space-y-1 text-sm">
                        {Object.entries(event.details).map(([key, value]) => (
                          <div key={key}>
                            <span className="font-medium">{key.replace(/_/g, ' ')}:</span>{' '}
                            <span className="text-muted-foreground">{String(value)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-muted-foreground py-8">
                No history available
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryModalOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssetReportsPage;